import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Sparkles,
  Package,
  FileText,
  Megaphone,
  Store,
  DollarSign,
  ShoppingBag,
  Images,
  Palette,
  Inbox,
  BarChart3,
  Settings2,
  LogOut,
  MessageCircle,
  LayoutGrid,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { APOLLO_LOGO } from "@/lib/brand";

const NAV = [
  { label: "Dashboard", to: "/", icon: LayoutDashboard, testId: "nav-dashboard" },
  { label: "Brand Center", to: "/brand", icon: Sparkles, testId: "nav-brand" },
  { label: "Products", to: "/products", icon: Package, testId: "nav-products" },
  { label: "Strains", to: "/strains", icon: Sparkles, testId: "nav-strains" },
  { label: "Marketing", to: "/marketing", icon: Megaphone, testId: "nav-marketing" },
  { label: "Retail Toolkit", to: "/retail", icon: Store, testId: "nav-retail" },
  { label: "Menu Designer", to: "/menu-designer", icon: LayoutGrid, testId: "nav-menu-designer" },
  { label: "Wholesale Toolkit", to: "/sales", icon: DollarSign, testId: "nav-sales", roles: ["admin"] },
  { label: "Merchandise", to: "/merch", icon: ShoppingBag, testId: "nav-merch" },
];

const ADMIN_NAV = [
  { label: "Media Manager", to: "/library", icon: Images, testId: "nav-library" },
  { label: "Artwork", to: "/artwork", icon: Palette, testId: "nav-artwork" },
  { label: "Analytics", to: "/analytics", icon: BarChart3, testId: "nav-analytics" },
  { label: "Admin", to: "/admin", icon: Settings2, testId: "nav-admin" },
];

export default function Sidebar({ mobileOpen = false, onMobileClose }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const canSee = (roles) => !roles || roles.includes(user.role) || user.role === "admin";

  // Apollo bars — subtle background pattern: single low-opacity white diagonals.
  const APOLLO_BARS = `repeating-linear-gradient(
    -45deg,
    rgba(255,255,255,0.08) 0px,
    rgba(255,255,255,0.08) 7px,
    transparent 7px,
    transparent 96px
  )`;

  return (
    <aside
      className={`w-[240px] shrink-0 border-r border-border bg-surface flex flex-col h-screen top-0 z-30
        fixed md:sticky
        transition-transform duration-300 ease-out
        ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
      data-testid="sidebar"
    >
      <button
        onClick={() => nav("/")}
        className="relative z-10 px-6 py-8 border-b border-border flex flex-col items-start gap-2 hover:opacity-80 transition bg-surface"
        data-testid="sidebar-logo"
      >
        <img src={APOLLO_LOGO} alt="Apollo" className="h-10 w-auto max-w-full object-contain dark:invert-0 invert" style={{ display: "block" }} />
        <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-muted-foreground">Brand Portal</span>
      </button>

      <nav className="relative flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
        {/* Diagonal Apollo bars — sit BEHIND the nav items, do not extend into the logo header */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: APOLLO_BARS, backgroundSize: "auto" }}
          data-testid="sidebar-bars"
        />
        <div className="relative z-10 space-y-0.5">
          <div className="px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Workspace</div>
          {NAV.filter((i) => canSee(i.roles)).map((item) => (
            <NavItem key={item.to} {...item} />
          ))}

          {(user.role === "admin") && (
            <>
              <div className="px-3 pt-6 pb-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Manage</div>
              {ADMIN_NAV.map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </>
          )}
        </div>
      </nav>

      <div className="relative z-10 border-t border-border p-3">
        <div className="flex items-center gap-3 p-2">
          <div className="w-8 h-8 rounded-sm bg-foreground text-background flex items-center justify-center font-display font-bold text-sm">
            {user.name?.[0]?.toUpperCase() || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold truncate" data-testid="sidebar-user-name">{user.name}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{user.role}</div>
          </div>
          <button
            onClick={logout}
            className="p-1.5 hover:bg-surface-elev rounded-sm text-muted-foreground hover:text-foreground transition"
            data-testid="sidebar-logout"
            aria-label="Logout"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}

function NavItem({ to, label, icon: Icon, testId }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      data-testid={testId}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition
        ${isActive
          ? "bg-foreground/10 dark:bg-white/10 text-foreground font-semibold"
          : "text-muted-foreground hover:text-foreground hover:bg-foreground/5 dark:hover:bg-white/5"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span className={`w-0.5 h-4 -ml-3 ${isActive ? "bg-[#E15C2C]" : "bg-transparent"}`} />
          <Icon className="h-4 w-4" />
          <span>{label}</span>
        </>
      )}
    </NavLink>
  );
}
